import { assign, fromPromise, setup } from "xstate"
import { set, cloneDeep, get } from "lodash-es"
import { Model, Services } from "@types"
import { ComputedRef, inject } from "vue"
import { FetchWrapper } from "@composables/useFetchWrapper"
import { consentDateFin, elecTexteDpeFost, toIsoDay } from "@constant"

/**
 * Type de chauffage dans le logement
 */
export type TypeChauffage =
    | "individuel"
    | "collectif"
    | "individuel_et_collectif"
    | "autre"
    | ""

/**
 * Système de chauffage électrique
 */
export type SystemChauffage =
    | "seche_serviette_elec"
    | "plancher_chauffant_elec"
    | "pac"
    | "radiateur_elec_fixe"
    | "radiateur_elec_mobile"
    | "autre"

/**
 * Type d'énergie utilisée dans le logement
 */
export type EnergyType = "elec" | "gaz" | "fuel" | "bois" | "autre"

/**
 * Usage de l'énergie utilisée dans le logement
 */
export type UsageEnergy =
    | "chauffage"
    | "eau_chaude"
    | "cuisson"
    | "autres_usages"

/**
 * Type de pompe à chaleur
 */
export type PacType =
    | "pac_air_air"
    | "pac_air_eau"
    | "pac_eau_eau"
    | "pac_hybride"
    | "autre"

export type ClimType = "clim_mobile" | "clim_fixe" | "autre"

export type TravauxType =
    | "isolation_combles_toiture"
    | "isolation_murs"
    | "isolation_plancher"
    | "fenetres_portes_fenetres"
    | "pac_air_eau"
    | "pac_eau_eau_sol_eau"
    | "pac_hybride"
    | "pac_air_air"
    | "chauffage_bois"
    | "chaudiere_biomasse"
    | "systeme_solaire_combine"
    | "chauffe_eau_thermodynamique"

type Context = {
    /**
     * Message d'erreur
     */
    error: string
    /**
     * Numéro de l'étape
     */
    step: number
    /**
     * Progression de l'étape
     */
    stepProgress: number
    /**
     * Identifiant du sous formulaire en cours
     */
    formId: string
    /**
     * Étape à laquelle rediriger
     */
    skipToStep: string | null
    /**
     * Identifiant du dossier CSTB
     *
     */
    _id_cstb_record: string
    /**
     * Prénom de l'utilisateur
     */
    firstname: string
    /**
     * Nom de l'utilisateur
     */
    lastname: string
    /**
     * Email de l'utilisateur
     */
    username: string
    /**
     * Numéro de téléphone de l'utilisateur
     */
    phone?: string
    /**
     * Numéro de PDL de l'utilisateur
     */
    pdl_idsource: string
    /**
     * Adresse du logement
     */
    address: {
        /**
         * Rue de l'adresse
         */
        street: string
        /**
         * Code postal de l'adresse
         */
        zipcode: string
        /**
         * Ville de l'adresse
         */
        city: string
        /**
         * Pays de l'adresse
         */
        country: string
        /**
         * Code postal de l'INSEE
         */
        citycode?: string
    }
    /**
     * Type de logement
     */
    type_logement: "maison" | "appartement" | ""
    /**
     * Type de chauffage dans l'appartement
     */
    type_chauffage: TypeChauffage
    /**
     * Type d'eau chaude sanitaire dans l'appartement
     */
    type_eau_chaude_sanitaire: TypeChauffage
    /**
     * Période de l'ensemble des travaux
     */
    periode_travaux: [Date, Date] | null
    /**
     * Nombre de personnes vivant dans le logement
     */
    nombre_occupant: number | null
    /**
     * Nombre de personnes vivant dans le logement de moins de 18 ans
     */
    nombre_occupant_0_18_ans: number | null
    /**
     * Nombre de personnes vivant dans le logement entre 18 et 60 ans
     */
    nombre_occupant_18_60_ans: number | null
    /**
     * Nombre de personnes vivant dans le logement de plus de 60 ans
     */
    nombre_occupant_60_ans: number | null
    /**
     * Surface du logement en m2
     */
    surface_logement: number | null
    /**
     * Type d'énergie utilisée avant les travaux
     */
    type_energie_avant_travaux: EnergyType[]
    /**
     * Type d'énergie utilisée après les travaux
     */
    type_energie_apres_travaux: EnergyType[]
    /**
     * Usage de l'énergie électrique utilisée avant les travaux
     */
    usage_energie_elec_avant_travaux: UsageEnergy[]
    /**
     * Usage de l'énergie électrique utilisée après les travaux
     */
    usage_energie_elec_apres_travaux: UsageEnergy[]
    /**
     * Usage de l'énergie gaz utilisée avant les travaux
     */
    usage_energie_gaz_avant_travaux: UsageEnergy[]
    /**
     * Usage de l'énergie gaz utilisée après les travaux
     */
    usage_energie_gaz_apres_travaux: UsageEnergy[]
    /**
     * Identifiant du compteur de gaz
     */
    pce_idsource?: string
    /**
     * Date de début du contract de gaz
     */
    date_from_data?: Date
    /**
     * Mode de chauffage avant les travaux
     */
    chauffage_principale_avant_travaux?: string
    /**
     * Mode de chauffage après les travaux
     */
    chauffage_principale_apres_travaux?: string
    /**
     * Type de climatisation avant les travaux
     */
    type_climatisation_avant_travaux?: ClimType
    /**
     * Type de climatisation après les travaux
     */
    type_climatisation_apres_travaux?: ClimType
    /**
     * Type de système de chauffage électrique avant les travaux
     */
    type_systeme_chauffage_elec_avant_travaux?: SystemChauffage[]
    /**
     * Type de système de chauffage électrique après les travaux
     */
    type_systeme_chauffage_elec_apres_travaux?: SystemChauffage[]
    /**
     * Type de pompe à chaleur avant les travaux
     */
    type_pac_avant_travaux?: PacType[]
    /**
     * Type de pompe à chaleur après les travaux
     */
    type_pac_apres_travaux?: PacType[]
    /**
     * Période d'absence des occupants avant ou après les travaux
     */
    periode_abscence?: { work: "before" | "after"; period: [Date, Date] }[]
    /**
     * Types de travaux sélectionnés
     */
    travaux_types: TravauxType[]

    /**
     * Erreur lors de la signature du consentement du PCE
     */
    error_sign_pce: { code: string; message: string; errorDetail: Error } | null
    /**
     * Erreur lors de la signature du consentement du PDL
     */
    error_sign_pdl: { code: string; message: string; errorDetail: Error } | null
    /**
     * Erreur lors de la recherche du PDL
     */
    error_pdl_search: {
        code: string
        message: string
        errorDetail: Error
    } | null

    /**
     * Case d'autorisation de l'étape gaz (DEV-2274). Remise à `false` à
     * chaque entrée dans l'étape : une case n'est jamais pré-cochée.
     */
    consent_pce: boolean
    /**
     * Autorisation Enedis de l'étape électricité (DEV-2274).
     */
    consent_pdl: boolean
    /**
     * Autorisation « travaux, DPE et météo » de l'étape électricité
     * (accords DPE et FOST, DEV-2274).
     */
    consent_travaux: boolean
    /**
     * Détail de l'étape électricité déplié : décide quelle phrase cochée est
     * enregistrée comme `text` des accords DPE et FOST (DEV-2274).
     */
    consent_detail_pdl: boolean
}

type Event =
    | { type: "BACK" }
    | { type: "SUBMIT" }
    | { type: "REDIRECT" }
    | { type: "MANUAL_SEARCH" }
    | { type: "AUTO_SEARCH" }
    | { type: "SEARCH_PDL" }
    | { type: "UPDATE_FIELD"; field: string; value: unknown }
    | {
          type: "UPDATE_CONTEXT_FROM_API"
          data: Model["CSTBRecord"]
      }
    | { type: "UPDATE_SKIP_TO_STEP"; value: string | null }

/**
 * Temporise pour qu'une étape dure au moins une seconde (lisibilité du
 * chargement). Au-delà d'une seconde, ne rajoute RIEN : l'ancienne version
 * attendait une seconde fois la durée déjà écoulée (DEV-2274).
 */
function wait(start: number) {
    const delta = new Date().getTime() - start
    return new Promise((resolve) => {
        setTimeout(
            () => {
                resolve(true)
            },
            delta < 1000 ? 1000 - delta : 0
        )
    })
}

/**
 * Deux dates de début de contrat désignent-elles le même jour ? (DEV-2274)
 *
 * Le contexte porte une `Date` (sélecteur) ou la chaîne ISO rendue par l'API ;
 * la comparaison stricte d'avant (`!==` entre une chaîne et une `Date`) était
 * toujours vraie : chaque soumission supprimait, recréait et re-signait la
 * source PCE. On compare le jour normalisé AAAA-MM-JJ des deux côtés.
 */
export function isSameDay(a: unknown, b: unknown): boolean {
    const da = toIsoDay(a)
    return da !== null && da === toIsoDay(b)
}

/** Un accord DPE ou FOST signé et non retiré existe-t-il ? */
function hasActiveEntry(entries: unknown): boolean {
    return (
        Array.isArray(entries) &&
        entries.some(
            (c: { signed?: boolean; dismissed?: boolean } | null) =>
                c?.signed === true && c?.dismissed !== true
        )
    )
}

/**
 * Les accords DPE ET FOST du premier bâtiment existent-ils ? (DEV-2274)
 * Ils ne sont re-signés que s'ils manquent.
 */
export function hasActiveDpeAndFost(record: any): boolean {
    const consent = record?.buildings?.[0]?.consent
    return (
        hasActiveEntry(consent?.consent_dpe) &&
        hasActiveEntry(consent?.consent_fost)
    )
}

/**
 * Reçu gaz (décision 7, DEV-2274) : la source PCE a un consentement actif et
 * ni le compteur ni la date de début n'ont changé. Pas de case, pas d'appel
 * de signature.
 */
export function isPceConsentUnchanged(
    pce:
        | { _id_consent?: string; idsource?: string; date_from_data?: unknown }
        | null
        | undefined,
    idsource: unknown,
    dateFromData: unknown
): boolean {
    return (
        !!pce?._id_consent &&
        !!idsource &&
        pce.idsource === idsource &&
        isSameDay(pce.date_from_data, dateFromData)
    )
}

/**
 * Reçu électricité (décision 7, DEV-2274) : consentement PDL actif sur le
 * même compteur ET accords DPE et FOST existants. Sinon, bloc normal.
 */
export function isPdlConsentReceipt(
    pdl: { _id_consent?: string; idsource?: string } | null | undefined,
    idsource: unknown,
    record: any
): boolean {
    return (
        !!record?._id &&
        !!pdl?._id_consent &&
        !!idsource &&
        pdl.idsource === idsource &&
        hasActiveDpeAndFost(record)
    )
}

/**
 * Code d'erreur du cas « un dossier existe déjà pour cet email ».
 * Ce cas n'a PAS de réessai : rejouer la création rejouerait le même refus.
 */
export const ALREADY_EXIST_CODE = "already-exist"

/**
 * Le ménage a-t-il répondu à l'écran « Type de logement » ? (DEV-1675)
 *
 * Une maison suffit ; un appartement exige aussi le type de chauffage et
 * celui de l'eau chaude sanitaire. Le contexte rechargé depuis l'API porte
 * `null` (et non `""`) pour une question jamais posée : seule une valeur
 * réelle compte.
 * @param context - Les trois réponses de l'écran
 * @returns true si l'écran est complet
 */
export function hasTypeLogement(context: {
    type_logement?: string | null
    type_chauffage?: string | null
    type_eau_chaude_sanitaire?: string | null
}): boolean {
    if (context.type_logement === "maison") return true
    if (context.type_logement !== "appartement") return false
    return !!context.type_chauffage && !!context.type_eau_chaude_sanitaire
}

/**
 * Traduit l'échec de `POST /cstb-record` en code d'erreur exploitable (DEV-1660).
 *
 * Ce que l'API rend réellement sur un email déjà enregistré (api-ww,
 * `src/modules/cstb-record/cstb-record.controller.ts` + `src/filters/
 * http-exception.filter.ts`) :
 *
 *     HTTP 400
 *     { detail: {
 *         message: "Une erreur est survenue lors de l'ajout des utilisateurs",
 *         code: "error-ajout",
 *         context: { message: "L'email est déjà enregistré" } } }
 *
 * Deux conséquences, toutes deux structurantes :
 * - `code` vaut `error-ajout` pour TOUT refus de création, pas seulement le
 *   doublon : le seul discriminant est `context.message`. Le code était
 *   auparavant écrit en dur à `already-exist` pour n'importe quelle erreur,
 *   ce qui affichait « votre email est déjà enregistré » sur une panne réseau.
 * - l'`_id` du dossier existant n'est PAS renvoyé, et aucune route de lecture
 *   par identité (email / token du mail) n'est joignable par le formulaire
 *   (`findOneByUsername` n'est exposé que par `POST /mail/unsubscribe` et par
 *   l'export-viewer authentifié par cookie). On ne PEUT donc pas reprendre le
 *   dossier existant depuis le formulaire : la seule sortie est le lien du
 *   mail d'invitation, qui porte `?_id_cstb_record=`. Voir DEV-1701.
 *
 * @param error - L'erreur portée par le `FetchWrapper` de création
 * @returns Le code, le message et le détail à poser dans `error_sign_pdl`
 */
export function classifyCreateCSTBRecordError(error: unknown): {
    code: string
    message: string
    errorDetail: any
} {
    const serviceError = error as
        | { code?: string; message?: string; context?: Record<string, any> }
        | null
        | undefined

    const contextMessage = serviceError?.context?.message
    const isDuplicate =
        serviceError?.code === ALREADY_EXIST_CODE ||
        (typeof contextMessage === "string" &&
            contextMessage.includes("déjà enregistré"))

    if (isDuplicate) {
        return {
            code: ALREADY_EXIST_CODE,
            message: "Un dossier existe déjà pour cette adresse email",
            errorDetail: error,
        }
    }

    return {
        // Le code réel rendu par l'API quand il existe, `erreur_creation` sinon.
        code: serviceError?.code || "erreur_creation",
        message: "Erreur lors de la création",
        errorDetail: error,
    }
}

// TODO - ajouter les variables depuis le store dans la machine
export const useFormMachine = ({
    fetchCreateCSTBRecord,
    fetchSignConsent,
    fetchDeleteSource,
    fetchCreateSource,
    fetchUpdateCSTBRecordUser,
    fetchUpdateCSTBRecordBuildingInfo,
    fetchSignFostAndDpeConsent,
    identifyTrackingUser,
    cstbRecord,
    pdl,
    pce,
}: {
    fetchCreateCSTBRecord: FetchWrapper
    fetchSignConsent: FetchWrapper
    fetchCreateSource: FetchWrapper
    fetchDeleteSource: FetchWrapper
    fetchUpdateCSTBRecordUser: FetchWrapper
    fetchUpdateCSTBRecordBuildingInfo: FetchWrapper
    fetchSignFostAndDpeConsent: FetchWrapper
    identifyTrackingUser: (userId: string) => void
    buildingId: ComputedRef<string | null>
    cstbRecord: any
    pdl: ComputedRef<{
        _id_source: string
        _id_consent: string
        idsource: string
        idtype: string
    } | null>
    pce: ComputedRef<{
        _id_source: string
        _id_consent: string
        idsource: string
        idtype: string
        date_from_data: Date
    } | null>
}) => {
    // Récupère le service de recherche de PDL
    const { $searchPoint, $searchAddress } = inject<Services>("services")! || {}

    /**
     * Fonction permettant de gérer la sauvegarde des info du dossier CSTB
     * @param redirectStep - L'étape à laquelle rediriger après la sauvegarde
     * @returns
     */
    function handleSaveSubStates(
        redirectStep: string | { target: string; guard?: any; actions?: any }[],
        { steps }: { steps: any } = {
            steps: {},
        }
    ) {
        const onDone =
            typeof redirectStep === "string"
                ? { target: redirectStep }
                : {
                      target: "checkConditions",
                  }

        const subSteps = {
            initial: "idle",
            states: {
                idle: {
                    on: {
                        SUBMIT: "updateField",
                    },
                },
                updateField: {
                    invoke: {
                        id: "update",
                        src: "updateBuildingInfoActor" as const,
                        input: ({ context }: { context: Context }) => context,
                        onDone,
                        onError: {
                            target: "error",
                        },
                    },
                },
                error: {
                    on: {
                        SUBMIT: "updateField",
                    },
                },
                ...steps,
            },
        } as {
            initial: string
            states: Record<string, any>
        }

        // Si le redirectStep est un tableau, on ajoute une condition
        if (Array.isArray(redirectStep)) {
            subSteps.states.checkConditions = {
                always: redirectStep,
            }
        }

        return subSteps
    }

    /**
     * Configuration de la machine d'état
     */
    const config = {
        types: {
            context: {} as Context,
            events: {} as Event,
        },
        guards: {
            /**
             * Vérifie si le logement contient du gaz avant ou après les travaux
             * @param context - Contexte de la machine
             * @returns true si le logement contient du gaz, false sinon
             */
            has_gaz: function ({ context }: { context: Context }) {
                return (
                    context.type_energie_avant_travaux.includes("gaz") ||
                    context.type_energie_apres_travaux.includes("gaz")
                )
            },
            /**
             * Vérifie si le logement contient de l'électricité avant ou après les travaux
             * @param param0
             */
            has_only_elec: function ({ context }: { context: Context }) {
                return (
                    context.type_energie_avant_travaux.length === 1 &&
                    context.type_energie_avant_travaux.includes("elec") &&
                    context.type_energie_apres_travaux.length === 1 &&
                    context.type_energie_apres_travaux.includes("elec")
                )
            },
            is_invalid: function ({ context }: { context: Context }) {
                // Si c'est une maison, on ne peut pas avoir de chauffage collectif
                if (context.type_logement === "maison") return false

                // Sinon, on vérifie si le chauffage est collectif
                return (
                    !["individuel", "autre"].includes(context.type_chauffage) ||
                    !["individuel", "autre"].includes(
                        context.type_eau_chaude_sanitaire
                    )
                )
            },
            canSubmitAddress: function ({ context }: { context: Context }) {
                return (
                    context.address.street !== "" &&
                    context.address.zipcode !== "" &&
                    context.address.city !== "" &&
                    context.address.country !== " " &&
                    context.username !== "" &&
                    context.firstname !== "" &&
                    context.lastname !== ""
                )
            },
            canSubmitTypeLogement: function ({
                context,
            }: {
                context: Context
            }) {
                return (context.type_logement as string) !== ""
            },
            /**
             * La signature du consentement a-t-elle échoué ? (DEV-1660)
             *
             * `error_sign_pdl` est remis à `null` à l'entrée de l'état `sign` :
             * une valeur non nulle après l'appel signifie donc que CET appel
             * a échoué — que l'acteur ait rejeté (`onError`) ou qu'il ait
             * résolu en portant `{ error }` (échec métier).
             * @param context - Contexte de la machine
             * @returns true si la dernière signature a échoué
             */
            hasSignConsentError: function ({ context }: { context: Context }) {
                return context.error_sign_pdl !== null
            },
            /**
             * L'échec est-il « un dossier existe déjà pour cet email » ? (DEV-1660)
             *
             * Ce cas se distingue de tous les autres par une chose : réessayer
             * ne peut PAS marcher. La création est refusée parce que le dossier
             * existe, l'API ne rend pas son `_id` et aucune route de lecture
             * par identité n'est joignable par le formulaire — le contexte
             * repart donc identique et le refus est identique. Le router vers
             * l'état générique `error`, dont « Suivant » relance la signature,
             * fabriquerait une boucle. D'où un état dédié, sans réessai.
             * @param context - Contexte de la machine
             * @returns true si le dossier existe déjà pour cet email
             */
            hasAlreadyExistError: function ({ context }: { context: Context }) {
                return context.error_sign_pdl?.code === ALREADY_EXIST_CODE
            },
            /**
             * Vérifie si on peut aller à l'étape de signature du consentement.
             * - `skip_to_step` côté API, ou
             * - source PDL en base sans consentement, ou
             * - recherche PDL réussie (`tracking.pdl_search`), ou
             * - PDL connu dans le contexte (ex. via `updateContextFromAPI`).
             * @param context - Contexte de la machine
             * @returns true si on peut aller à l'étape de signature du consentement, false sinon
             */
            canGoToSignConsent: function ({ context }: { context: Context }) {
                if (!context._id_cstb_record) return false
                if (context.skipToStep === "sign-consent") return true
                if (pdl.value) {
                    return !pdl.value._id_consent
                }
                const pdlSearchSucceeded =
                    !!cstbRecord.value?.tracking?.pdl_search?.some(
                        (entry: { status: string; idsource?: string }) =>
                            entry.status === "success" && !!entry.idsource
                    )
                if (pdlSearchSucceeded) return true
                return !!context.pdl_idsource
            },
            /**
             * L'étape gaz peut-elle signer ? (DEV-2274) Case cochée, ou reçu
             * (consentement actif sur un compteur et une date inchangés).
             * Sans l'un ni l'autre, `SUBMIT` mène à `consent_missing`.
             */
            canSubmitPceConsent: function ({ context }: { context: Context }) {
                return (
                    context.consent_pce === true ||
                    isPceConsentUnchanged(
                        pce.value,
                        context.pce_idsource,
                        context.date_from_data
                    )
                )
            },
            /**
             * L'étape électricité peut-elle signer ? (DEV-2274) Les DEUX
             * autorisations données, ou reçu (PDL, DPE et FOST déjà signés).
             */
            canSubmitPdlConsent: function ({ context }: { context: Context }) {
                return (
                    (context.consent_pdl === true &&
                        context.consent_travaux === true) ||
                    isPdlConsentReceipt(
                        pdl.value,
                        context.pdl_idsource,
                        cstbRecord.value
                    )
                )
            },
            canGoToStep2: function ({ context }: { context: Context }) {
                return (
                    !!context._id_cstb_record &&
                    !!pdl.value &&
                    !!pdl.value._id_consent
                )
            },
            /**
             * Faut-il (re)présenter l'écran « Type de logement » ? (DEV-1675)
             *
             * Dossier à l'étape 2 (mêmes conditions que `canGoToInfoGeneral`)
             * sans type de logement réel. Vaut aussi pour un dossier par
             * ailleurs complet : de juin 2025 à octobre 2026 l'écran n'était
             * jamais présenté, ces ménages doivent y répondre en revenant.
             */
            needsTypeLogement: function ({ context }: { context: Context }) {
                return (
                    !!context._id_cstb_record &&
                    !!context.pdl_idsource &&
                    !hasTypeLogement(context)
                )
            },
            canGoToInfoGeneral: function ({ context }: { context: Context }) {
                return !!context._id_cstb_record && !!context.pdl_idsource
            },
            canGoToTypeEnergy: function ({ context }: { context: Context }) {
                return (
                    !!context._id_cstb_record &&
                    context.periode_travaux?.length === 2 &&
                    context.nombre_occupant !== null
                )
            },
            canGoToEnergyElec: function ({ context }: { context: Context }) {
                return (
                    !!context._id_cstb_record &&
                    context.type_energie_apres_travaux?.length > 0 &&
                    context.type_energie_avant_travaux?.length > 0
                )
            },
            canGoToGaz: function ({ context }: { context: Context }) {
                return (
                    !!context._id_cstb_record &&
                    context.type_energie_apres_travaux?.includes("gaz") &&
                    context.type_energie_avant_travaux?.includes("gaz") &&
                    context.usage_energie_elec_apres_travaux.length > 0 &&
                    context.usage_energie_elec_avant_travaux.length > 0
                )
            },
            canGoToEnergyGaz: function ({ context }: { context: Context }) {
                return (
                    !!context._id_cstb_record &&
                    !!context.pce_idsource &&
                    !!context.date_from_data
                )
            },
            canGoToStep3: function ({ context }: { context: Context }) {
                // `hasTypeLogement` : `null` (contexte rechargé de l'API) ne
                // passe plus, ni un appartement sans chauffage ni ECS (DEV-1675).
                let mustHave =
                    !!context._id_cstb_record &&
                    hasTypeLogement(context) &&
                    context.periode_travaux?.length === 2 &&
                    context.nombre_occupant !== null &&
                    context.type_energie_avant_travaux.length > 0 &&
                    context.type_energie_apres_travaux.length > 0 &&
                    context.usage_energie_elec_apres_travaux.length > 0 &&
                    context.usage_energie_elec_avant_travaux.length > 0

                if (
                    context.type_energie_apres_travaux.includes("gaz") ||
                    context.type_energie_avant_travaux.includes("gaz")
                ) {
                    mustHave =
                        mustHave &&
                        !!context.pce_idsource &&
                        !!context.date_from_data

                    if (context.type_energie_apres_travaux.includes("gaz")) {
                        mustHave =
                            mustHave &&
                            context.usage_energie_gaz_apres_travaux.length > 0
                    }
                    if (context.type_energie_avant_travaux.includes("gaz")) {
                        mustHave =
                            mustHave &&
                            context.usage_energie_gaz_avant_travaux.length > 0
                    }
                }

                return mustHave
            },
        },
        actions: {
            updateSkipToStep: assign(
                ({ context, event }: { context: Context; event: Event }) => {
                    if (event.type === "UPDATE_SKIP_TO_STEP") {
                        return { skipToStep: event.value }
                    }
                    return context
                }
            ),
            updateContextFromAPI: assign(
                ({ context, event }: { context: Context; event: Event }) => {
                    if (event.type === "UPDATE_CONTEXT_FROM_API") {
                        // copie le contexte pour ne pas le muter directement
                        const newContext = cloneDeep(context)

                        // Récupère le document représentant le dossier CSTB
                        const { data } = event

                        // Met à jour le contexte avec les données du dossier CSTB
                        newContext._id_cstb_record = data._id

                        // Les données de l'utilisateur
                        newContext.firstname = data.firstname
                        newContext.lastname = data.lastname
                        newContext.username = data.username
                        newContext.phone = data.phone

                        if (data.buildings.length) {
                            const building = data.buildings[0]
                            // PDL

                            const pdlFound = building.sources?.find(
                                (source) => source.idtype === "pdl"
                            )
                            if (pdlFound) {
                                newContext.pdl_idsource = pdlFound.idsource
                            } else {
                                const trackingPdlSuccess =
                                    data.tracking.pdl_search?.find(
                                        (search) => search.status === "success"
                                    )
                                if (trackingPdlSuccess) {
                                    newContext.pdl_idsource =
                                        trackingPdlSuccess.idsource
                                }
                            }

                            // PCE
                            const pceFound = building.sources?.find(
                                (source) => source.idtype === "pce"
                            )
                            if (pceFound) {
                                newContext.pce_idsource = pceFound.idsource
                                newContext.date_from_data =
                                    pceFound.date_from_data
                            }

                            // Adresse
                            newContext.address.street =
                                building?.address?.street
                            newContext.address.zipcode =
                                building?.address?.zipcode
                            newContext.address.city = building?.address?.city
                            newContext.address.country =
                                building?.address?.country || "France"

                            const info_logement =
                                data.buildings[0].info_logement

                            // Les réponses du questionnaire
                            ;[
                                "type_logement",
                                "type_chauffage",
                                "type_eau_chaude_sanitaire",
                                "periode_travaux",
                                "nombre_occupant",
                                "nombre_occupant_0_18_ans",
                                "nombre_occupant_18_60_ans",
                                "nombre_occupant_60_ans",
                                "surface_logement",
                                "type_energie_avant_travaux",
                                "type_energie_apres_travaux",
                                "usage_energie_elec_avant_travaux",
                                "usage_energie_elec_apres_travaux",
                                "usage_energie_gaz_avant_travaux",
                                "usage_energie_gaz_apres_travaux",
                                "chauffage_principale_avant_travaux",
                                "chauffage_principale_apres_travaux",
                                "type_climatisation_avant_travaux",
                                "type_climatisation_apres_travaux",
                                "type_systeme_chauffage_elec_avant_travaux",
                                "type_systeme_chauffage_elec_apres_travaux",
                                "type_pac_avant_travaux",
                                "type_pac_apres_travaux",
                                "periode_abscence",
                                "travaux_types",
                            ].forEach((key) => {
                                // Si la clé existe dans le contexte, on la met à jour
                                if ((info_logement as any)[key] !== undefined) {
                                    set(
                                        newContext,
                                        key,
                                        (info_logement as any)[key]
                                    )
                                }
                            })
                        }

                        // renvoie le nouveau contexte
                        return newContext
                    }
                    return context
                }
            ),
            updateField: assign(
                ({ context, event }: { context: Context; event: Event }) => {
                    // Update the context with the new field value
                    if (event.type === "UPDATE_FIELD") {
                        // si on trouve le champ dans le contexte en utilisant lodash
                        // on le met à jour
                        if (get(context, event.field) !== undefined) {
                            // copie le contexte pour ne pas le muter directement
                            const newContext = cloneDeep(context)
                            // met à jour le champ
                            set(newContext, event.field, event.value)
                            // renvoie le nouveau contexte
                            return newContext
                        }
                    }
                    return context
                }
            ),
        },
        actors: {
            searchPdlActor: fromPromise(
                async ({ input }: { input: Context }): Promise<string> => {
                    // Si on a pas de address.citycode alors on fait une recherche d'abord d'adresse afin de récupérer le citycode
                    let citycode = input.address.citycode
                    if (!citycode) {
                        const address = await $searchAddress(
                            `${input.address.street} ${input.address.zipcode} ${input.address.city}`
                        )
                        if (address.length) {
                            citycode = address[0].citycode
                        }
                    }

                    // Si on a pas de citycode alors on ne peut pas faire de recherche de PDL
                    if (!citycode) {
                        throw new Error("Aucun code postal trouvé")
                    }

                    // On fait une recherche de PDL
                    const { idsource } = await $searchPoint({
                        street: input.address.street,
                        zipcode: input.address.zipcode,
                        citycode,
                        lastname: input.lastname,
                    })

                    if (!idsource) throw new Error("Aucun PDL trouvé")

                    return idsource
                }
            ),
            /**
             * Fonction permettant de gérer la validation de l'étape 1 avec
             * la signature du consentement.
             *
             * Plus précisément, cette fonction permet de :
             * - Créer le dossier CSTB si il n'existe pas
             * - Signer le consentement si il n'est pas déjà signé
             * - Mettre à jour le bâtiment si il existe
             * - Mettre à jour l'utilisateur si il existe
             *
             */
            signPdlConsentActor: fromPromise(
                async ({
                    input,
                }: {
                    input: Context
                }): Promise<{ _id_cstb_record: string; error?: any }> => {
                    /**
                     * Fonction utilitaire permettant de créer le dossier CSTB
                     */
                    async function createCSTBRecord() {
                        // Création du dossier CSTB
                        await fetchCreateCSTBRecord.fetch({
                            username: input.username,
                            firstname: input.firstname,
                            lastname: input.lastname,
                            phone: input.phone,
                            address: input.address,
                            pdl_idsource: input.pdl_idsource,
                        })

                        // Si une erreur est survenue lors de la création du dossier CSTB alors on lève une erreur
                        if (fetchCreateCSTBRecord.hasError.value) {
                            return {
                                // Le code n'est plus écrit en dur : il vient de
                                // ce que l'API a réellement rendu (DEV-1660).
                                // `.value` et non le Ref : le classifieur lit
                                // `code` et `context` sur l'erreur elle-même.
                                error: classifyCreateCSTBRecordError(
                                    fetchCreateCSTBRecord.error.value
                                ),
                                _id_cstb_record: cstbRecord.value
                                    ?._id as string,
                            }
                        }

                        // Rattache le suivi au dossier du ménage
                        identifyTrackingUser(cstbRecord.value?._id as string)
                    }

                    /**
                     * Fonction utilitaire permettant de créer la source
                     */
                    async function createSource() {
                        await fetchCreateSource.fetch({
                            idtype: "pdl",
                            idsource: input.pdl_idsource,
                        })

                        if (fetchCreateSource.hasError.value) {
                            // Retourne pas une erreur mais un message d'erreur
                            return {
                                error: {
                                    code: "create-error",
                                    message: "Erreur lors de la création",
                                    errorDetail: fetchCreateSource.error,
                                },
                                _id_cstb_record: cstbRecord?._id as string,
                            }
                        }
                    }

                    /**
                     * Fonction utilitaire permettant de signer le consentement
                     */
                    async function signConsent() {
                        await fetchSignConsent.fetch({
                            idtype: "pdl",
                            idsource: input.pdl_idsource,
                            consent_checked: input.consent_pdl === true,
                        })

                        if (fetchSignConsent.hasError.value) {
                            // Retourne pas une erreur mais un message d'erreur
                            return {
                                error: {
                                    code: "sign-error",
                                    message: "Erreur lors de la signature",
                                    errorDetail: fetchSignConsent.error,
                                },
                                _id_cstb_record: cstbRecord?._id as string,
                            }
                        }
                    }

                    /**
                     * Fonction utilitaire permettant de supprimer la source
                     */
                    async function removeSource() {
                        await fetchDeleteSource.fetch({
                            idtype: "pdl",
                            idsource: pdl.value?.idsource,
                        })

                        if (fetchDeleteSource.hasError.value) {
                            // Retourne pas une erreur mais un message d'erreur
                            return {
                                error: {
                                    code: "delete-error",
                                    message: "Erreur lors de la suppression",
                                    errorDetail: fetchDeleteSource.error,
                                },
                                _id_cstb_record: cstbRecord?._id as string,
                            }
                        }
                    }

                    /**
                     * Fonction utilitaire permettant de mettre à jour le bâtiment
                     */
                    async function updateBuilding() {
                        await fetchUpdateCSTBRecordBuildingInfo.fetch({
                            address: input.address,
                            name: `Logement de ${input.firstname} ${input.lastname}`,
                        })

                        if (fetchUpdateCSTBRecordBuildingInfo.hasError.value) {
                            // Retourne pas une erreur mais un message d'erreur
                            return {
                                error: {
                                    code: "update-error",
                                    message: "Erreur lors de la mise à jour",
                                    errorDetail:
                                        fetchUpdateCSTBRecordBuildingInfo.error,
                                },
                                _id_cstb_record: cstbRecord?._id as string,
                            }
                        }
                    }

                    /**
                     * Fonction utilitaire permettant de mettre à jour l'utilisateur
                     */
                    async function updateUser() {
                        const payload = {
                            firstname: input.firstname,
                            lastname: input.lastname,
                            phone: input.phone,
                        } as Record<string, string>

                        if (input.username) payload.username = input.username

                        await fetchUpdateCSTBRecordUser.fetch(payload)

                        if (fetchUpdateCSTBRecordUser.hasError.value) {
                            // Retourne pas une erreur mais un message d'erreur
                            return {
                                error: {
                                    code: "update-error",
                                    message: "Erreur lors de la mise à jour",
                                    errorDetail:
                                        fetchUpdateCSTBRecordUser.error,
                                },
                                _id_cstb_record: cstbRecord?._id as string,
                            }
                        }
                    }

                    /**
                     * Fonction utilitaire permettant de signer le consentement FOST et DPE
                     */
                    async function signFostAndDpeConsent() {
                        // Le texte enregistré est la phrase affichée et
                        // cochée, produite par le module de textes (DEV-2274).
                        await fetchSignFostAndDpeConsent.fetch({
                            text: elecTexteDpeFost({
                                fin: consentDateFin(cstbRecord.value),
                                deplie: input.consent_detail_pdl === true,
                            }),
                            consent_checked: input.consent_travaux === true,
                        })

                        if (fetchSignFostAndDpeConsent.hasError.value) {
                            // Retourne pas une erreur mais un message d'erreur
                            return {
                                error: {
                                    code: "sign-fost-dpe-error",
                                    message: "Erreur lors de la signature",
                                    errorDetail:
                                        fetchSignFostAndDpeConsent.error,
                                },
                                _id_cstb_record: cstbRecord?._id as string,
                            }
                        }
                    }

                    let start = new Date()

                    let result = {
                        error: null,
                        _id_cstb_record: cstbRecord.value?._id as string,
                    } as Record<string, any> | undefined

                    async function handleNewCSTBRecord() {
                        // Création du dossier CSTB
                        start = new Date()
                        result = await createCSTBRecord()
                        await wait(start.getTime())

                        if (result?.error) {
                            return {
                                _id_cstb_record: cstbRecord.value
                                    ?._id as string,
                                error: result?.error,
                            }
                        }

                        // Signature FOST et DPE
                        start = new Date()
                        result = await signFostAndDpeConsent()
                        await wait(start.getTime())

                        if (result?.error) {
                            return {
                                _id_cstb_record: cstbRecord.value
                                    ?._id as string,
                                error: result?.error,
                            }
                        }

                        // Signature consentement PDL
                        start = new Date()
                        result = await signConsent()
                        await wait(start.getTime())

                        if (result?.error) {
                            return {
                                _id_cstb_record: cstbRecord.value
                                    ?._id as string,
                                error: result?.error,
                            }
                        }

                        return {
                            _id_cstb_record: cstbRecord.value?._id as string,
                        }
                    }

                    async function handleUpdateCSTBRecord() {
                        // Mise à jour de l'utilisateur
                        start = new Date()
                        result = await updateUser()
                        await wait(start.getTime())

                        if (result?.error) {
                            return {
                                _id_cstb_record: cstbRecord.value
                                    ?._id as string,
                                error: result?.error,
                            }
                        }

                        // Mise à jour du bâtiment
                        start = new Date()
                        result = await updateBuilding()
                        await wait(start.getTime())

                        if (result?.error) {
                            return {
                                _id_cstb_record: cstbRecord.value
                                    ?._id as string,
                                error: result?.error,
                            }
                        }

                        // Signe le consentement si nécessaire
                        const shouldSignConsent =
                            !pdl.value?._id_consent ||
                            pdl.value?.idsource !== input.pdl_idsource

                        // Suppression de la source si nécessaire
                        const shouldRemoveSource =
                            pdl.value?._id_source &&
                            pdl.value?._id_consent &&
                            pdl.value?.idsource !== input.pdl_idsource

                        const shouldCreateSource =
                            pdl.value?.idsource !== input.pdl_idsource ||
                            shouldRemoveSource

                        if (shouldRemoveSource) {
                            start = new Date()
                            result = await removeSource()
                            await wait(start.getTime())

                            if (result?.error) {
                                return {
                                    _id_cstb_record: cstbRecord.value
                                        ?._id as string,
                                    error: result?.error,
                                }
                            }
                        }

                        // S'il n'y a pas de source alors on la crée
                        if (shouldCreateSource) {
                            start = new Date()
                            result = await createSource()
                            await wait(start.getTime())

                            if (result?.error) {
                                return {
                                    _id_cstb_record: cstbRecord.value
                                        ?._id as string,
                                    error: result?.error,
                                }
                            }
                        }

                        if (shouldSignConsent) {
                            result = await signConsent()

                            if (result?.error) {
                                return {
                                    _id_cstb_record: cstbRecord.value
                                        ?._id as string,
                                    error: result?.error,
                                }
                            }
                        }

                        // Accords DPE et FOST re-signés SEULEMENT s'ils manquent
                        // (DEV-2274) : les re-signer à chaque passage
                        // révoquait des accords valides.
                        if (!hasActiveDpeAndFost(cstbRecord.value)) {
                            start = new Date()
                            result = await signFostAndDpeConsent()

                            await wait(start.getTime())

                            if (result?.error) {
                                return {
                                    _id_cstb_record: cstbRecord.value
                                        ?._id as string,
                                    error: result?.error,
                                }
                            }
                        }

                        return {
                            _id_cstb_record: cstbRecord.value?._id as string,
                        }
                    }

                    // S'il n'y a pas de dossier CSTB alors on le crée
                    // + on signe le consentement
                    if (!input._id_cstb_record) {
                        return await handleNewCSTBRecord()
                    }

                    // Si on a un dossier CSTB alors on le met à jour
                    if (input._id_cstb_record) {
                        return await handleUpdateCSTBRecord()
                    }

                    throw new Error(
                        "Aucun dossier CSTB trouvé, impossible de signer le consentement"
                    )
                }
            ),
            signPceConsentActor: fromPromise(
                async ({
                    input,
                }: {
                    input: Context
                }): Promise<void | {
                    error: {
                        code: string
                        message: string
                        errorDetail: Error
                    }
                }> => {
                    // S'il n'y a pas de dossier CSTB alors on le crée
                    // + on signe le consentement
                    if (!input._id_cstb_record) {
                        throw new Error(
                            "Erreur lors de la signature du consentement, dossier CSTB introuvable"
                        )
                    }

                    /**
                     * Fonction utilitaire permettant de signer le consentement
                     */
                    async function signConsent() {
                        await fetchSignConsent.fetch({
                            idtype: "pce",
                            idsource: input.pce_idsource,
                            consent_checked: input.consent_pce === true,
                        })

                        if (fetchSignConsent.hasError.value) {
                            // Retourne pas une erreur mais un message d'erreur
                            return {
                                error: {
                                    code: "sign-error",
                                    message: "Erreur lors de la signature",
                                    errorDetail: fetchSignConsent.error,
                                },
                            }
                        }
                    }

                    /**
                     * Fonction utilitaire permettant de créer la source
                     */
                    async function createSource() {
                        await fetchCreateSource.fetch({
                            idtype: "pce",
                            idsource: input.pce_idsource,
                            // Jour saisi, en AAAA-MM-JJ (DEV-2274) : une `Date`
                            // sérialisée partait la veille à 22:00 UTC.
                            date_from_data: toIsoDay(input.date_from_data),
                        })

                        if (fetchCreateSource.hasError.value) {
                            // Retourne pas une erreur mais un message d'erreur
                            return {
                                error: {
                                    code: "create-error",
                                    message: "Erreur lors de la création",
                                    errorDetail: fetchCreateSource.error,
                                },
                            }
                        }
                    }

                    /**
                     * Fonction utilitaire permettant de supprimer la source
                     */
                    async function removeSource() {
                        await fetchDeleteSource.fetch({
                            idtype: "pce",
                            idsource: pce.value?.idsource,
                        })

                        if (fetchDeleteSource.hasError.value) {
                            // Retourne pas une erreur mais un message d'erreur
                            return {
                                error: {
                                    code: "delete-error",
                                    message: "Erreur lors de la suppression",
                                    errorDetail: fetchDeleteSource.error,
                                },
                            }
                        }
                    }

                    // Jour de début de contrat inchangé ? Comparaison des jours
                    // normalisés (DEV-2274) : l'ancienne comparaison chaîne
                    // contre Date était toujours fausse et re-signait tout.
                    const sameDate = isSameDay(
                        pce.value?.date_from_data,
                        input.date_from_data
                    )

                    // Signe le consentement si nécessaire
                    const shouldSignConsent =
                        !pce.value?._id_consent ||
                        pce.value?.idsource !== input.pce_idsource ||
                        !sameDate

                    // Suppression de la source si nécessaire
                    const shouldRemoveSource =
                        ((pce.value?._id_source &&
                            pce.value?._id_consent &&
                            pce.value?.idsource !== input.pce_idsource) ||
                            !sameDate) &&
                        !!pce.value

                    const shouldCreateSource =
                        pce.value?.idsource !== input.pce_idsource ||
                        shouldRemoveSource

                    let start = new Date()

                    let result = {
                        error: null,
                    } as Record<string, any> | undefined

                    if (shouldRemoveSource) {
                        start = new Date()
                        result = await removeSource()

                        if (result?.error) {
                            return { error: result?.error }
                        }
                        await wait(start.getTime())
                    }

                    if (shouldCreateSource) {
                        start = new Date()
                        result = await createSource()

                        if (result?.error) {
                            return { error: result?.error }
                        }
                        await wait(start.getTime())
                    }

                    // S'il y a déjà un dossier CSTB alors on signe le consentement
                    if (shouldSignConsent) {
                        result = await signConsent()

                        if (result?.error) {
                            return { error: result?.error }
                        }
                    }
                }
            ),
            updateBuildingInfoActor: fromPromise(
                async ({ input }: { input: Context }): Promise<void> => {
                    // On met à jour le dossier CSTB avec les nouvelles informations
                    await fetchUpdateCSTBRecordBuildingInfo.fetch({
                        info_logement: {
                            type_logement: input.type_logement,
                            type_chauffage: input.type_chauffage,
                            type_eau_chaude_sanitaire:
                                input.type_eau_chaude_sanitaire,
                            periode_travaux: input.periode_travaux,
                            nombre_occupant: input.nombre_occupant,
                            nombre_occupant_0_18_ans:
                                input.nombre_occupant_0_18_ans,
                            nombre_occupant_18_60_ans:
                                input.nombre_occupant_18_60_ans,
                            nombre_occupant_60_ans:
                                input.nombre_occupant_60_ans,
                            surface_logement: input.surface_logement,
                            type_energie_avant_travaux:
                                input.type_energie_avant_travaux,
                            type_energie_apres_travaux:
                                input.type_energie_apres_travaux,
                            usage_energie_elec_avant_travaux:
                                input.usage_energie_elec_avant_travaux,
                            usage_energie_elec_apres_travaux:
                                input.usage_energie_elec_apres_travaux,
                            usage_energie_gaz_avant_travaux:
                                input.usage_energie_gaz_avant_travaux,
                            usage_energie_gaz_apres_travaux:
                                input.usage_energie_gaz_apres_travaux,
                            pce_idsource: input.pce_idsource,
                            date_from_data: input.date_from_data,
                            chauffage_principale_avant_travaux:
                                input.chauffage_principale_avant_travaux,
                            chauffage_principale_apres_travaux:
                                input.chauffage_principale_apres_travaux,
                            type_climatisation_avant_travaux:
                                input.type_climatisation_avant_travaux,
                            type_climatisation_apres_travaux:
                                input.type_climatisation_apres_travaux,
                            type_systeme_chauffage_elec_avant_travaux:
                                input.type_systeme_chauffage_elec_avant_travaux,
                            type_systeme_chauffage_elec_apres_travaux:
                                input.type_systeme_chauffage_elec_apres_travaux,
                            type_pac_avant_travaux:
                                input.type_pac_avant_travaux,
                            type_pac_apres_travaux:
                                input.type_pac_apres_travaux,
                            periode_abscence: input.periode_abscence,
                            travaux_types: input.travaux_types,
                        },
                    })
                }
            ),
        },
    }

    /**
     * Les états de la machine et le context
     */
    const states = {
        context: {
            // INFORMATION SUR LES ETAPES DU FORMULAIRES
            error: "",
            step: 0,
            stepProgress: 0,
            formId: "",
            skipToStep: null,

            // INFORMATIONS SUR LE DOSSIER CSTB
            _id_cstb_record: "",
            firstname: "",
            lastname: "",
            username: "",
            phone: "",
            pdl_idsource: "",
            address: {
                street: "",
                zipcode: "",
                city: "",
                country: "",
            },
            type_logement: "",
            type_chauffage: "",
            type_eau_chaude_sanitaire: "",
            periode_travaux: null,
            nombre_occupant: null,
            nombre_occupant_0_18_ans: null,
            nombre_occupant_18_60_ans: null,
            nombre_occupant_60_ans: null,
            surface_logement: null,
            type_energie_avant_travaux: [],
            type_energie_apres_travaux: [],
            usage_energie_elec_avant_travaux: [],
            usage_energie_elec_apres_travaux: [],
            usage_energie_gaz_avant_travaux: [],
            usage_energie_gaz_apres_travaux: [],
            pce_idsource: null,
            date_from_data: null,
            chauffage_principale_avant_travaux: "",
            chauffage_principale_apres_travaux: "",
            type_climatisation_avant_travaux: "",
            type_climatisation_apres_travaux: "",
            type_systeme_chauffage_elec_avant_travaux: [],
            type_systeme_chauffage_elec_apres_travaux: [],
            type_pac_avant_travaux: [],
            type_pac_apres_travaux: [],
            periode_abscence: [],
            travaux_types: [],

            // ERREURS DES INVOKES
            error_sign_pce: null,
            error_sign_pdl: null,
            error_pdl_search: null,

            // CASES D'AUTORISATION (DEV-2274), jamais pré-cochées
            consent_pce: false,
            consent_pdl: false,
            consent_travaux: false,
            consent_detail_pdl: false,
        },
        id: "Form",
        initial: "idle",
        states: {
            idle: {
                on: {
                    UPDATE_CONTEXT_FROM_API: {
                        actions: "updateContextFromAPI",
                    },
                    UPDATE_SKIP_TO_STEP: {
                        actions: "updateSkipToStep",
                    },
                    REDIRECT: [
                        // Si on peut aller à l'étape de signature du consentement, on redirige vers cette étape
                        {
                            target: "#Form.Step1.FormSignConsent",
                            guard: {
                                type: "canGoToSignConsent",
                            },
                        },
                        // Sans type de logement, l'écran Type de logement
                        // passe avant tout autre écran de l'étape 2, même pour
                        // un dossier par ailleurs complet (DEV-1675)
                        {
                            target: "#Form.Step2.FormTypeLogement",
                            guard: {
                                type: "needsTypeLogement",
                            },
                        },
                        // Si on peut aller à l'étape 3
                        {
                            target: "#Form.Step3",
                            guard: {
                                type: "canGoToEnergyGaz",
                            },
                        },
                        // Si on peut aller à l'étape 2 pour la partie usage du gaz
                        {
                            target: "#Form.Step2.FormEnergyGaz",
                            guard: {
                                type: "canGoToEnergyGaz",
                            },
                        },
                        // Si on peut aller à l'étape 2 pour la partie info sur le gaz
                        {
                            target: "#Form.Step2.FormGaz",
                            guard: {
                                type: "canGoToGaz",
                            },
                        },
                        // Si on peut aller à l'étape 2 pour la partie usage de l'énergie électrique
                        {
                            target: "#Form.Step2.FormEnergyElec",
                            guard: {
                                type: "canGoToEnergyElec",
                            },
                        },
                        // Si on peut aller à l'étape 2 pour la partie type d'énergie
                        {
                            target: "#Form.Step2.FormTypeEnergy",
                            guard: {
                                type: "canGoToTypeEnergy",
                            },
                        },
                        // Si on peut aller à l'étape 2 pour la partie info générale
                        {
                            target: "#Form.Step2.FormInfoLogementGeneral",
                            guard: {
                                type: "canGoToInfoGeneral",
                            },
                        },
                        // Si on peut aller à l'étape 2
                        {
                            target: "Step2",
                            guard: {
                                type: "canGoToStep2",
                            },
                        },
                        // Sinon on redirige vers l'étape 1
                        {
                            target: "Step1",
                        },
                    ],
                },
            },
            Step1: {
                entry: [
                    assign({
                        step: 0,
                        stepProgress: 0,
                    }),
                ],
                initial: "FormAddress",
                on: {
                    SUBMIT: {
                        target: "Step2",
                    },
                    UPDATE_CONTEXT_FROM_API: {
                        actions: "updateContextFromAPI",
                    },
                },
                states: {
                    FormAddress: {
                        initial: "idle",
                        entry: [
                            assign({
                                stepProgress: 35,
                                formId: "form-address",
                            }),
                        ],
                        on: {
                            UPDATE_FIELD: {
                                actions: "updateField",
                            },
                            SUBMIT: {
                                target: "FormSignConsent",
                            },
                            MANUAL_SEARCH: "FormAddress.manualsearch",
                            AUTO_SEARCH: "FormAddress.autosearch",
                        },
                        states: {
                            idle: {
                                on: {
                                    REDIRECT: [
                                        {
                                            target: "autosearch",
                                            guard: ({
                                                context,
                                            }: {
                                                context: Context
                                            }) =>
                                                !context.pdl_idsource &&
                                                !context._id_cstb_record,
                                        },
                                        {
                                            target: "manualsearch",
                                        },
                                    ],
                                },
                            },
                            // Quand on est en mode recherche automatique
                            autosearch: {
                                entry: [
                                    assign({
                                        error_pdl_search: null,
                                    }),
                                ],
                                initial: "idle",
                                states: {
                                    idle: {},
                                    search_pdl: {
                                        invoke: {
                                            id: "searchPdl",
                                            src: "searchPdlActor",
                                            input: ({
                                                context,
                                            }: {
                                                context: Context
                                            }) => context,
                                            onDone: {
                                                target: "idle",
                                                actions: assign({
                                                    pdl_idsource: ({ event }) =>
                                                        event.output,
                                                }),
                                            },
                                            onError: {
                                                target: "#Form.Step1.FormAddress.manualsearch",
                                                actions: assign({
                                                    error_pdl_search: ({
                                                        event,
                                                    }) => {
                                                        return event?.error
                                                    },
                                                }),
                                            },
                                        },
                                    },
                                    not_found: {},
                                },
                                on: {
                                    SEARCH_PDL: {
                                        target: "autosearch.search_pdl",
                                    },
                                },
                            },
                            manualsearch: {},
                        },
                    },
                    FormSignConsent: {
                        initial: "idle",
                        entry: [
                            assign({
                                stepProgress: 70,
                                formId: "form-sign-consent",
                                // Une case n'est jamais pré-cochée (DEV-2274).
                                consent_pdl: false,
                                consent_travaux: false,
                                consent_detail_pdl: false,
                            }),
                        ],
                        on: {
                            BACK: {
                                target: "FormAddress",
                            },
                            // Les cases d'autorisation passent par le contexte
                            // de la machine (DEV-2274).
                            UPDATE_FIELD: {
                                actions: "updateField",
                            },
                        },
                        states: {
                            /**
                             * `SUBMIT` n'a JAMAIS une garde seule (DEV-2274) :
                             * sans autorisation, la transition de repli mène
                             * au sous-état nommé `consent_missing`. Une garde
                             * seule laisserait l'événement remonter à
                             * `Step1.on.SUBMIT`, qui vise `Step2`.
                             */
                            idle: {
                                on: {
                                    SUBMIT: [
                                        {
                                            target: "sign",
                                            guard: "canSubmitPdlConsent",
                                        },
                                        { target: "consent_missing" },
                                    ],
                                },
                            },
                            /**
                             * Clic sans les deux autorisations (DEV-2274) : on
                             * reste sur l'étape, aucun appel de signature. La
                             * balise poste `Step1:FormSignConsent:consent_missing`.
                             */
                            consent_missing: {
                                on: {
                                    SUBMIT: [
                                        {
                                            target: "sign",
                                            guard: "canSubmitPdlConsent",
                                        },
                                        // Absorbé ici, ne remonte pas.
                                        { actions: [] },
                                    ],
                                },
                            },
                            sign: {
                                entry: [
                                    assign({
                                        error_sign_pdl: null,
                                    }),
                                ],
                                invoke: {
                                    id: "signConsent",
                                    src: "signPdlConsentActor",
                                    input: ({
                                        context,
                                    }: {
                                        context: Context
                                    }) => context,
                                    onDone: {
                                        target: "#Form.Step1.FormSignConsent.redirect",
                                        actions: assign({
                                            _id_cstb_record: ({
                                                context,
                                                event,
                                            }: {
                                                context: Context
                                                event: any
                                            }) => {
                                                return typeof event.output !==
                                                    "undefined" &&
                                                    !!event.output
                                                        ?._id_cstb_record
                                                    ? event.output
                                                          ?._id_cstb_record
                                                    : context._id_cstb_record
                                            },
                                            error_sign_pdl: ({
                                                event,
                                            }: {
                                                event: any
                                            }) => {
                                                return (
                                                    event.output?.error ?? null
                                                )
                                            },
                                        }),
                                    },
                                    onError: {
                                        actions: assign({
                                            error_sign_pdl: ({
                                                event,
                                            }: {
                                                event: any
                                            }) => ({
                                                code: "actor-error",
                                                message:
                                                    event.error?.message ??
                                                    "Erreur lors de la signature",
                                                errorDetail: event.error,
                                            }),
                                        }),
                                        target: "#Form.Step1.FormSignConsent.redirect",
                                    },
                                },
                            },
                            /**
                             * Aiguillage APRÈS l'appel de signature (DEV-1660).
                             *
                             * L'échec a deux formes et les deux comptent :
                             * - l'acteur REJETTE (réseau, exception) → `onError`
                             *   pose `error_sign_pdl` ;
                             * - l'acteur RÉSOUT en portant `{ error }` (échec
                             *   métier : `already-exist`, création de source
                             *   refusée…) → `onDone` pose `error_sign_pdl`.
                             *
                             * Tant que `error_sign_pdl` n'est pas nul, la
                             * signature n'est PAS prouvée : on reste sur
                             * l'étape (état `error`), le ménage voit le message
                             * et peut réessayer. Sans ce garde, les deux
                             * branches menaient à `#Form.Step2` — écran de fin
                             * et mail `final` servis sur un échec, et aucune
                             * trace du passage (la balise DEV-1061 postait
                             * `Step2`).
                             */
                            redirect: {
                                always: [
                                    {
                                        target: "#Form.Step1.FormSignConsent.already_exist",
                                        guard: "hasAlreadyExistError",
                                    },
                                    {
                                        target: "#Form.Step1.FormSignConsent.error",
                                        guard: "hasSignConsentError",
                                    },
                                    {
                                        target: "#Form.Step2",
                                    },
                                ],
                            },
                            /**
                             * Un dossier existe déjà pour cet email (DEV-1660).
                             *
                             * Cul-de-sac assumé et NOMMÉ, pas subi : `SUBMIT`
                             * n'est volontairement pas gérée ici, parce que la
                             * relancer rejouerait la même création avec le même
                             * contexte et récolterait le même refus. L'API ne
                             * rend pas l'`_id` du dossier existant et le
                             * formulaire n'a aucune route de lecture par
                             * identité (DEV-1701) : la seule reprise possible
                             * est le lien du mail d'invitation, qui porte
                             * `?_id_cstb_record=` et fait repartir le
                             * formulaire sur la branche « mise à jour ».
                             * `FormSignConsent.vue` n'affiche donc pas de
                             * bouton « Suivant » ici, seulement le message et
                             * « Retour » (hérité de `FormSignConsent.on.BACK`,
                             * pour corriger un email mal saisi).
                             *
                             * La balise DEV-1061 poste la valeur d'état :
                             * `tracking.form_progress.last_step` porte
                             * `Step1:FormSignConsent:already_exist`, distinct
                             * de l'échec générique — ces doublons sont donc
                             * comptables côté back.
                             */
                            already_exist: {
                                on: {
                                    /**
                                     * ABSORBE `SUBMIT`, sans cible et sans
                                     * action. Ne PAS déclarer cette transition
                                     * serait un piège : un `SUBMIT` non traité
                                     * par la sous-étape remonte à
                                     * `Step1.on.SUBMIT`, qui vise `Step2` —
                                     * soit l'écran de fin servi sans aucune
                                     * signature, exactement le faux succès que
                                     * DEV-1660 corrige. Une transition
                                     * targetless est traitée ICI et ne remonte
                                     * pas.
                                     */
                                    SUBMIT: { actions: [] },
                                },
                            },
                            /**
                             * Échec de signature : le ménage reste ici. Le
                             * message est rendu par `FormSignConsent.vue`
                             * (`error_sign_pdl`) et « Suivant » relance la
                             * signature. Cet état est aussi la TRACE : la
                             * balise de progression (DEV-1061) poste la valeur
                             * d'état, donc `tracking.form_progress.last_step`
                             * porte `Step1:FormSignConsent:error`.
                             */
                            error: {
                                on: {
                                    SUBMIT: [
                                        {
                                            target: "sign",
                                            guard: "canSubmitPdlConsent",
                                        },
                                        { target: "consent_missing" },
                                    ],
                                },
                            },
                        },
                    },
                },
            },
            Step2: {
                // L'écran Type de logement ouvre l'étape 2 (DEV-1675) : il
                // était sauté depuis le 19/06/2025 (`da3bc73`).
                initial: "FormTypeLogement",
                entry: [
                    assign({
                        step: 1,
                        stepProgress: 0,
                        error_sign_pdl: null,
                    }),
                ],
                on: {
                    UPDATE_FIELD: {
                        actions: "updateField",
                    },
                    BACK: {
                        target: "Step1.FormSignConsent",
                    },
                    SUBMIT: {
                        target: "Step3",
                    },
                    UPDATE_CONTEXT_FROM_API: {
                        actions: "updateContextFromAPI",
                    },
                },
                states: {
                    FormTypeLogement: {
                        entry: [
                            assign({
                                stepProgress: 15,
                                formId: "form-type-logement",
                            }),
                        ],
                        on: {
                            UPDATE_FIELD: {
                                actions: "updateField",
                            },
                        },
                        ...handleSaveSubStates([
                            {
                                target: "#Form.Step2.FormInvalid",
                                guard: {
                                    type: "is_invalid",
                                },
                            },
                            {
                                target: "#Form.Step2.FormInfoLogementGeneral",
                            },
                        ]),
                    },
                    FormInfoLogementGeneral: {
                        entry: [
                            assign({
                                stepProgress: 35,
                                formId: "form-info-logement-general",
                            }),
                        ],
                        on: {
                            UPDATE_FIELD: {
                                actions: "updateField",
                            },

                            BACK: {
                                target: "FormTypeLogement",
                            },
                        },
                        ...handleSaveSubStates("#Form.Step2.FormTypeEnergy"),
                    },
                    FormTypeEnergy: {
                        entry: [
                            assign({
                                stepProgress: 50,
                                formId: "form-type-energy",
                            }),
                        ],
                        on: {
                            UPDATE_FIELD: {
                                actions: "updateField",
                            },
                            SUBMIT: {
                                target: "FormEnergyElec",
                            },
                            BACK: {
                                target: "FormInfoLogementGeneral",
                            },
                        },
                        ...handleSaveSubStates([
                            {
                                target: "#Form.Step3",
                                guard: {
                                    type: "has_only_elec",
                                },
                                actions: assign({
                                    usage_energie_elec_avant_travaux: [
                                        "chauffage",
                                        "eau_chaude",
                                        "cuisson",
                                    ],
                                    usage_energie_elec_apres_travaux: [
                                        "chauffage",
                                        "eau_chaude",
                                        "cuisson",
                                    ],
                                }),
                            },
                            {
                                target: "#Form.Step2.FormEnergyElec",
                            },
                        ]),
                    },
                    FormEnergyElec: {
                        entry: [
                            assign({
                                stepProgress: 70,
                                formId: "form-energy-elec",
                            }),
                        ],
                        on: {
                            UPDATE_FIELD: {
                                actions: "updateField",
                            },
                            BACK: {
                                target: "FormTypeEnergy",
                            },
                        },
                        ...handleSaveSubStates([
                            {
                                target: "#Form.Step2.FormGaz",
                                guard: {
                                    type: "has_gaz",
                                },
                            },
                            {
                                target: "#Form.Step3",
                            },
                        ]),
                    },
                    FormGaz: {
                        initial: "idle",
                        entry: [
                            assign({
                                stepProgress: 70,
                                formId: "form-gaz",
                                // Une case n'est jamais pré-cochée (DEV-2274).
                                consent_pce: false,
                            }),
                        ],
                        on: {
                            UPDATE_FIELD: {
                                actions: "updateField",
                            },
                            BACK: {
                                target: "FormEnergyElec",
                            },
                        },
                        states: {
                            /**
                             * `SUBMIT` n'a JAMAIS une garde seule (DEV-2274) :
                             * sans autorisation, repli explicite vers
                             * `consent_missing`. Une garde seule laisserait
                             * l'événement remonter à `Step2.on.SUBMIT`, qui
                             * vise `Step3` : l'étape serait passée sans
                             * autorisation ni signature.
                             */
                            idle: {
                                on: {
                                    SUBMIT: [
                                        {
                                            target: "sign",
                                            guard: "canSubmitPceConsent",
                                        },
                                        { target: "consent_missing" },
                                    ],
                                },
                            },
                            /**
                             * Clic sans case (DEV-2274) : on reste sur l'étape,
                             * aucun appel de signature. La balise poste
                             * `Step2:FormGaz:consent_missing`.
                             */
                            consent_missing: {
                                on: {
                                    SUBMIT: [
                                        {
                                            target: "sign",
                                            guard: "canSubmitPceConsent",
                                        },
                                        // Absorbé ici, ne remonte pas.
                                        { actions: [] },
                                    ],
                                },
                            },
                            sign: {
                                // L'erreur d'un essai précédent ne survit pas
                                // au suivant (DEV-2274).
                                entry: [
                                    assign({
                                        error_sign_pce: null,
                                    }),
                                ],
                                invoke: {
                                    id: "signConsent",
                                    src: "signPceConsentActor",
                                    input: ({
                                        context,
                                    }: {
                                        context: Context
                                    }) => context,
                                    onDone: {
                                        target: "#Form.Step2.FormGaz.redirect",
                                        actions: assign({
                                            error_sign_pce: ({
                                                event,
                                            }: {
                                                event: any
                                            }) => event.output?.error ?? null,
                                        }),
                                    },
                                    onError: {
                                        target: "error",
                                        // L'erreur est posée et affichée
                                        // (DEV-2274) : elle restait nulle.
                                        actions: assign({
                                            error_sign_pce: ({
                                                event,
                                            }: {
                                                event: any
                                            }) => ({
                                                code: "actor-error",
                                                message:
                                                    event.error?.message ??
                                                    "Erreur lors de la signature",
                                                errorDetail: event.error,
                                            }),
                                        }),
                                    },
                                },
                            },
                            redirect: {
                                always: [
                                    {
                                        target: "#Form.Step2.FormGaz.error",
                                        guard: ({
                                            context,
                                        }: {
                                            context: Context
                                        }) => !!context.error_sign_pce,
                                    },
                                    {
                                        target: "#Form.Step2.FormEnergyGaz",
                                    },
                                ],
                            },
                            error: {
                                on: {
                                    SUBMIT: [
                                        {
                                            target: "sign",
                                            guard: "canSubmitPceConsent",
                                        },
                                        { target: "consent_missing" },
                                    ],
                                },
                            },
                        },
                    },
                    FormEnergyGaz: {
                        entry: [
                            assign({
                                formId: "form-energy-gaz",
                                stepProgress: 90,
                            }),
                        ],
                        on: {
                            UPDATE_FIELD: {
                                actions: "updateField",
                            },
                            BACK: {
                                target: "FormGaz",
                            },
                        },
                        ...handleSaveSubStates("#Form.Step3"),
                    },
                    FormInvalid: {
                        entry: [
                            assign({
                                formId: "form-invalid",
                            }),
                        ],
                        on: {
                            BACK: {
                                target: "FormTypeLogement",
                            },
                        },
                    },
                },
            },
            Step3: {
                ...handleSaveSubStates("#Form.Finally"),
                entry: [
                    assign({
                        step: 2,
                        stepProgress: 0,
                        formId: "form-optional",
                    }),
                ],
                on: {
                    UPDATE_FIELD: {
                        actions: "updateField",
                    },
                    BACK: [
                        {
                            target: "Step2.FormEnergyGaz",
                            guard: {
                                type: "has_gaz",
                            },
                        },
                        {
                            target: "Step2.FormEnergyElec",
                        },
                    ],
                },
            },
            Finally: {
                entry: [
                    assign({
                        step: 3,
                        stepProgress: 100,
                    }),
                ],
            },
        },
    }

    const machine = setup(config as any).createMachine(states as any)

    return {
        machine,
    }
}
